﻿import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminButton, AdminPagination, AdminSearchInput, AdminStatusBadge, AdminTable, AdminTd, AdminTr } from "@/components/admin";
import type { AdminStatusTone } from "@/lib/adminCodeDisplay";
import { formatDateTime } from "@/lib/format";
import type { AdminLifeSignalItem } from "@/types.ts";
interface MonitoringListProps {
    targets: AdminLifeSignalItem[];
    searchQuery: string;
    onSearchChange: (v: string) => void;
    activeFilter: string;
    totalCount: number;
    onRefresh: () => void;
}
type LifeSignalMeta = {
    label: string;
    tone: AdminStatusTone;
};
const getScheduleResponseMeta = (item: AdminLifeSignalItem): LifeSignalMeta => {
    switch (item.latestScheduleResponseStatus) {
        case "RESPONDED":
            return { label: "응답 완료", tone: "success" };
        case "NO_RESPONSE":
            return { label: "무응답", tone: "warning" };
        case "SCHEDULED":
            return { label: "예정", tone: "info" };
        default:
            return { label: "데이터 없음", tone: "neutral" };
    }
};
const getSoundMeta = (item: AdminLifeSignalItem): LifeSignalMeta => {
    const status = item.latestSoundStatus;
    if (status === "RISK") {
        return { label: item.latestSoundStatusShortName ?? item.latestSoundStatusName ?? "확인 필요", tone: "warning" };
    }
    if (status === "QUIET")
        return { label: item.latestSoundStatusShortName ?? item.latestSoundStatusName ?? "조용함", tone: "success" };
    if (status)
        return { label: item.latestSoundStatusShortName ?? item.latestSoundStatusName ?? status, tone: "info" };
    return { label: "데이터 없음", tone: "neutral" };
};
const getDeviceMeta = (item: AdminLifeSignalItem): LifeSignalMeta => {
    switch (item.latestDeviceStatus) {
        case "ONLINE":
            return { label: item.latestDeviceStatusShortName ?? item.latestDeviceStatusName ?? "온라인", tone: "success" };
        case "OFFLINE":
            return { label: item.latestDeviceStatusShortName ?? item.latestDeviceStatusName ?? "오프라인", tone: "danger" };
        case "UNKNOWN":
            return { label: item.latestDeviceStatusShortName ?? item.latestDeviceStatusName ?? "확인 중", tone: "warning" };
        default:
            return { label: "데이터 없음", tone: "neutral" };
    }
};
const getEmergencyMeta = (item: AdminLifeSignalItem): LifeSignalMeta => {
    switch (item.latestEmergencyEventStatus) {
        case "OPEN":
            return { label: item.latestEmergencyEventStatusShortName ?? item.latestEmergencyEventStatusName ?? "확인 필요", tone: "danger" };
        case "ACKNOWLEDGED":
        case "IN_PROGRESS":
            return { label: item.latestEmergencyEventStatusShortName ?? item.latestEmergencyEventStatusName ?? "처리 중", tone: "warning" };
        case "RESOLVED":
            return { label: item.latestEmergencyEventStatusShortName ?? item.latestEmergencyEventStatusName ?? "완료", tone: "success" };
        default:
            return { label: "없음", tone: "neutral" };
    }
};
const latestAt = (item: AdminLifeSignalItem) => {
    const values = [item.latestScheduleResponseAt, item.latestSoundAt, item.latestDeviceStatusAt]
        .filter((value): value is string => Boolean(value));
    if (values.length === 0)
        return null;
    return values.sort((left, right) => right.localeCompare(left))[0];
};
const matchesFilter = (item: AdminLifeSignalItem, filter: string) => {
    if (filter === "all")
        return true;
    if (filter === "normal")
        return !item.needsAttention;
    if (filter === "check-needed")
        return item.needsAttention;
    if (filter === "no-response")
        return item.latestScheduleResponseStatus === "NO_RESPONSE";
    if (filter === "offline-device")
        return item.latestDeviceStatus === "OFFLINE";
    if (filter === "sound-check")
        return item.latestSoundStatus === "RISK";
    if (filter === "emergency")
        return item.latestEmergencyEventStatus === "OPEN";
    return true;
};
const MonitoringList = ({ targets, searchQuery, onSearchChange, activeFilter, totalCount, onRefresh, }: MonitoringListProps) => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const filtered = useMemo(() => {
        let result = targets.filter((item) => matchesFilter(item, activeFilter));
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            result = result.filter((item) => [item.elderlyName, item.seniorUserAccountId]
                .filter((value) => value != null)
                .some((value) => String(value).toLowerCase().includes(q)));
        }
        return result.sort((left, right) => Number(right.needsAttention) - Number(left.needsAttention));
    }, [targets, activeFilter, searchQuery]);
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIdx = (safePage - 1) * pageSize;
    const endIdx = Math.min(startIdx + pageSize, filtered.length);
    const pageItems = filtered.slice(startIdx, endIdx);
    return (<div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-list-check text-slate-500 text-sm"/>
          </div>
          <h3 className="text-sm font-semibold text-slate-700">모니터링 대상 목록</h3>
          <span className="text-xs text-slate-400">총 {totalCount}명</span>
          {activeFilter !== "all" && <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">필터 적용 중</span>}
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <AdminSearchInput value={searchQuery} onChange={(value) => {
            onSearchChange(value);
            setCurrentPage(1);
          }} placeholder="이름, ID, 요약 검색" className="min-w-0 flex-1 sm:w-56 sm:flex-none"/>
          <AdminButton type="button" variant="secondary" size="sm" icon="ri-refresh-line" onClick={onRefresh} className="shrink-0">새로고침</AdminButton>
        </div>
      </div>

      <AdminTable headers={["어르신", "일정 응답", "소리 분석", "기기 상태", "긴급 이벤트", "최근 업데이트", "상세"]} emptyMessage={pageItems.length === 0 ? "표시할 생활 모니터링 데이터가 없습니다." : undefined}>
            {pageItems.map((item, index) => {
            const scheduleResponse = getScheduleResponseMeta(item);
            const sound = getSoundMeta(item);
            const device = getDeviceMeta(item);
            const emergency = getEmergencyMeta(item);
            return (<AdminTr key={`${item.seniorUserAccountId}:${item.elderlyName}:${latestAt(item) ?? "no-latest"}:${index}`} className="hover:bg-slate-50/60">
                    <AdminTd>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold text-slate-800">{item.elderlyName}</span>
                        <span className="text-xs text-slate-400">ID {item.seniorUserAccountId}</span>
                      </div>
                    </AdminTd>
                    {[scheduleResponse, sound, device, emergency].map((meta, index) => (<AdminTd key={index}>
                        <AdminStatusBadge label={meta.label} tone={meta.tone} className="rounded px-1.5 text-xs"/>
                      </AdminTd>))}
                    <AdminTd>
                      <span className="text-xs text-slate-500">{formatDateTime(latestAt(item))}</span>
                    </AdminTd>
                    <AdminTd className="text-center">
                      <AdminButton type="button" variant="soft" size="sm" onClick={() => navigate(`/admin/users/seniors/${item.seniorUserAccountId}?tab=care`)}>상세</AdminButton>
                    </AdminTd>
                  </AdminTr>);
        })}
      </AdminTable>
      <AdminPagination currentPage={safePage} totalPages={totalPages} totalCount={filtered.length} pageSize={pageSize} itemLabel="명" onPageChange={setCurrentPage}/>
    </div>);
};
export default MonitoringList;

